import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/Button";

// Self-serve account-deletion surface for the signed-in user.
//
// The flow branches by level:
//   * Officer / supervisor (L1-L4 — anyone other than the owner):
//     submit a request that fans out to L1+ via the existing
//     send-notification path. The request is durable in
//     deletion_requests so it doesn't vanish if the supervisor
//     misses the notification.
//   * Owner: agencies require an owner at all times, so we don't
//     surface a request flow here. Instead we tell them to use
//     Settings → Transfer ownership first, then come back to file
//     a deletion request once they're no longer the owner.
//
// Required for Apple App Store Guideline 5.1.1(v) — every app that
// supports account creation must offer in-app deletion. ShiftManager
// qualifies for the managed-account carve-out (officer profiles are
// agency-provisioned), but the request mechanism still has to live
// inside the app.

export function AccountDeletionPanel() {
  const user = useAuth((s) => s.user);
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const pendingQ = useQuery({
    queryKey: ["my-deletion-request"],
    queryFn: api.getMyDeletionRequest,
    enabled: !!user && user.level !== "owner",
  });

  const submitMut = useMutation({
    mutationFn: () => api.requestAccountDeletion(reason.trim() || undefined),
    onSuccess: () => {
      setReason("");
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ["my-deletion-request"] });
    },
  });

  const cancelMut = useMutation({
    mutationFn: (id: string) => api.cancelMyDeletionRequest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-deletion-request"] }),
  });

  if (!user) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Delete account</h2>
        <p className="mt-1 text-xs text-slate-500">
          Your ShiftManager account is provisioned by your agency to
          satisfy scheduling and records-retention policies. To delete
          it, file a request below — your supervisor will review and
          act on it.
        </p>
      </div>

      {user.level === "owner" ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-900">
          <p>
            <strong>You're the agency owner.</strong> Every agency
            needs an owner at all times, so you can't file a deletion
            request directly. Transfer ownership to another supervisor
            in <strong>Settings → Transfer ownership</strong> first.
            Once you're no longer the owner, this card will let you
            file a request the same as everyone else.
          </p>
        </div>
      ) : pendingQ.data ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-900 space-y-2">
          <div>
            <strong>Request pending.</strong> Filed{" "}
            {new Date(pendingQ.data.requestedAt).toLocaleString()}.
            Your supervisor has been notified and will review it.
          </div>
          {pendingQ.data.reason && (
            <div className="italic">Reason: {pendingQ.data.reason}</div>
          )}
          <div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => cancelMut.mutate(pendingQ.data!.id)}
              disabled={cancelMut.isPending}
            >
              {cancelMut.isPending ? "Canceling…" : "Cancel request"}
            </Button>
          </div>
        </div>
      ) : confirmOpen ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-3 text-xs text-red-900 space-y-3">
          <p>
            <strong>This sends a deletion request to your supervisor.</strong>{" "}
            Once approved, you'll be signed out and your account will
            be disabled. Your schedule history stays on file for the
            agency's records.
          </p>
          <label className="block">
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-red-800 mb-1">
              Reason (optional)
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Leaving the agency, no longer use the app, etc."
              rows={3}
              className="w-full text-xs rounded border border-red-200 bg-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-red-400"
            />
          </label>
          {submitMut.error && (
            <div className="text-red-800 text-xs">
              {(submitMut.error as Error).message}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="danger"
              onClick={() => submitMut.mutate()}
              disabled={submitMut.isPending}
            >
              {submitMut.isPending ? "Submitting…" : "Submit request"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setConfirmOpen(false); setReason(""); }}
              disabled={submitMut.isPending}
            >
              Back
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button
            size="sm"
            variant="danger"
            onClick={() => setConfirmOpen(true)}
          >
            Request account deletion
          </Button>
        </div>
      )}
    </section>
  );
}
